import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon, PlusIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { ActionButton, EmptyState, Loader } from '@communityshare/ui-shared';
import { useDeleteVideo, useHomeOrder, useStaffVideos } from '../hooks/useStaffVideos';
import { useStaffPlaylists } from '../hooks/useStaffPlaylists';
import useSingleFlight from '../hooks/useSingleFlight';
import getErrorMessage from '../api/errorMessages';
import {
  canPin,
  getHomeVideos,
  moveDown,
  moveUp,
  pinVideo,
  unpinVideo,
  MAX_HOME_VIDEOS
} from '../utils/homeSet';
import filterVideos, { VIDEO_FILTERS, VideoFilters } from '../utils/filterVideos';
import { HomeVideoAudiences, isEligible } from '@/features/videos-common/audiences';
import { inputClassName } from './FormRow';
import VideosTable from './VideosTable';
import HomeVideos from './HomeVideos';
import VideoFormDialog from './VideoFormDialog';
import ImportPlaylistDialog from './ImportPlaylistDialog';
import PlaylistsSection from './PlaylistsSection';
import StatusMessage from './StatusMessage';

const HOME_FULL_MESSAGE_ID = 'staff-home-videos-full';
const PLAYLISTS_ANCHOR_ID = 'staff-playlists-heading';

export default function VideosPage() {
  const { t } = useTranslation();
  const videosQuery = useStaffVideos();
  const deleteVideo = useDeleteVideo();
  const homeOrder = useHomeOrder();
  const playlistsQuery = useStaffPlaylists();

  const [feedback, setFeedback] = useState(null);
  const [homeStatus, setHomeStatus] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // null: closed, { video: null }: add, { video }: edit
  const [formState, setFormState] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [filter, setFilter] = useState(VideoFilters.All);
  const [homeAudience, setHomeAudience] = useState(HomeVideoAudiences.Educator);

  const videos = useMemo(() => videosQuery.data || [], [videosQuery.data]);
  const filteredVideos = useMemo(() => filterVideos(videos, filter), [videos, filter]);
  const homeVideos = useMemo(() => getHomeVideos(videos, homeAudience), [videos, homeAudience]);
  const homeIds = homeVideos.map((video) => video.id);
  const audienceLabel = t(`staff_videos.audiences.${homeAudience}`);
  const hasEligibleVideos = useMemo(
    () => videos.some((video) => isEligible(video, homeAudience)),
    [videos, homeAudience]
  );
  // undefined until playlists load (nothing shown), null when the audience has none
  const homePlaylist = playlistsQuery.isSuccess
    ? (playlistsQuery.data || []).find((playlist) => playlist.audience === homeAudience) || null
    : undefined;
  const homeBusy = homeOrder.isLoading;

  // Always sends the selected audience's full ordered list; other audiences
  // are untouched. On failure the hook refetches, so the section reverts to
  // the server's saved order.
  const saveHomeOrder = useSingleFlight(async (nextIds) => {
    setHomeStatus(null);
    try {
      await homeOrder.mutateAsync({ audience: homeAudience, videoIds: nextIds });
      setHomeStatus({ tone: 'success', title: t('staff_videos.home.saved', { audience: audienceLabel }) });
    } catch (error) {
      const { detail } = getErrorMessage(error, t);
      setHomeStatus({ tone: 'error', title: t('staff_videos.home.save_error'), detail });
    }
  });

  function handleAudienceChange(audience) {
    if (audience === homeAudience) return;
    setHomeStatus(null);
    setHomeAudience(audience);
  }

  // Pins to the selected tab's audience.
  function handlePin(video) {
    if (!isEligible(video, homeAudience)) return;
    const result = pinVideo(homeIds, video.id);
    if (result.error === 'full') {
      setHomeStatus({
        tone: 'error',
        title: t('staff_videos.home.full', { audience: audienceLabel, max: MAX_HOME_VIDEOS })
      });
      return;
    }
    if (result.error) return;
    saveHomeOrder(result.ids);
  }

  function handleUnpin(video) {
    saveHomeOrder(unpinVideo(homeIds, video.id));
  }

  const handleDelete = useSingleFlight(async (video) => {
    setFeedback(null);
    setDeletingId(video.id);
    try {
      await deleteVideo.mutateAsync(video.id);
      setFeedback({ tone: 'success', title: t('staff_videos.list.deleted', { title: video.title }) });
    } catch (error) {
      const { message, detail } = getErrorMessage(error, t);
      setFeedback({ tone: 'error', title: message, detail });
    } finally {
      setDeletingId(null);
    }
  });

  function openAdd() {
    setFeedback(null);
    setFormState({ video: null });
  }

  function handleEdit(video) {
    setFeedback(null);
    setFormState({ video });
  }

  function handleSaved(message) {
    setFormState(null);
    setFeedback({ tone: 'success', title: message });
  }

  let content;
  if (videosQuery.isLoading) {
    content = (
      <div className="py-12 flex justify-center" role="status" aria-label={t('staff_videos.list.loading')}>
        <Loader />
      </div>
    );
  } else if (videosQuery.isError) {
    const { message, detail } = getErrorMessage(videosQuery.error, t);
    content = (
      <StatusMessage tone="error" title={t('staff_videos.list.load_error')} detail={detail || message}>
        <div className="mt-2">
          <ActionButton
            variant="secondary"
            loading={videosQuery.isFetching}
            loadingLabel={t('staff_videos.list.loading')}
            onClick={() => videosQuery.refetch()}
          >
            {t('staff_videos.list.retry')}
          </ActionButton>
        </div>
      </StatusMessage>
    );
  } else if (videos.length === 0) {
    content = (
      <EmptyState
        icon={VideoCameraIcon}
        title={t('staff_videos.list.empty_title')}
        description={t('staff_videos.list.empty_description')}
        action={t('staff_videos.add_video')}
        onAction={openAdd}
      />
    );
  } else if (filteredVideos.length === 0) {
    content = (
      <p className="text-sm text-cs-gray-700 bg-white border border-cs-gray-200 rounded-cs-10 p-4">
        {t('staff_videos.list.filter_empty')}
      </p>
    );
  } else {
    content = (
      <VideosTable
        videos={filteredVideos}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deletingId={deletingId}
        onPin={handlePin}
        onUnpin={handleUnpin}
        homeAudience={homeAudience}
        canPin={canPin(homeIds)}
        homeBusy={homeBusy}
        pinDisabledReasonId={HOME_FULL_MESSAGE_ID}
      />
    );
  }

  return (
    <div className="space-y-8">
      <Helmet>
        <title>{t('staff_videos.page_title')}</title>
      </Helmet>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex flex-col space-y-1">
          <h1 className="text-3xl font-semibold text-cs-gray-900">
            {t('staff_videos.page_title')}
          </h1>
          <p className="text-base text-cs-gray-500">
            {t('staff_videos.page_subtitle')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <ActionButton
            variant="secondary"
            onClick={() => {
              setFeedback(null);
              setImportOpen(true);
            }}
          >
            <span className="inline-flex items-center gap-1">
              <ArrowDownTrayIcon className="size-5" aria-hidden="true" />
              {t('staff_videos.import_playlist')}
            </span>
          </ActionButton>
          <ActionButton variant="primary" onClick={openAdd}>
            <span className="inline-flex items-center gap-1">
              <PlusIcon className="size-5" aria-hidden="true" />
              {t('staff_videos.add_video')}
            </span>
          </ActionButton>
        </div>
      </div>

      <div aria-live="polite">
        {feedback && (
          <StatusMessage tone={feedback.tone} title={feedback.title} detail={feedback.detail} />
        )}
      </div>

      {videosQuery.isSuccess && (
        <HomeVideos
          audience={homeAudience}
          onAudienceChange={handleAudienceChange}
          videos={homeVideos}
          hasEligibleVideos={hasEligibleVideos}
          playlist={homePlaylist}
          playlistsAnchorId={PLAYLISTS_ANCHOR_ID}
          busy={homeBusy}
          status={homeStatus}
          fullMessageId={HOME_FULL_MESSAGE_ID}
          onMoveUp={(index) => saveHomeOrder(moveUp(homeIds, index))}
          onMoveDown={(index) => saveHomeOrder(moveDown(homeIds, index))}
          onUnpin={handleUnpin}
        />
      )}

      <section aria-labelledby="staff-videos-list-heading" className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <h2 id="staff-videos-list-heading" className="text-xl font-semibold text-cs-gray-900">
            {t('staff_videos.list.heading')}
          </h2>
          {videos.length > 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="staff-videos-filter" className="text-sm font-semibold text-cs-gray-900 whitespace-nowrap">
                {t('staff_videos.list.filter.label')}
              </label>
              <select
                id="staff-videos-filter"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                className={inputClassName(false)}
              >
                {VIDEO_FILTERS.map((option) => (
                  <option key={option} value={option}>
                    {t(`staff_videos.list.filter.${option}`)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {content}
      </section>

      <PlaylistsSection headingId={PLAYLISTS_ANCHOR_ID} />

      {formState && (
        <VideoFormDialog
          video={formState.video}
          onClose={() => setFormState(null)}
          onSaved={handleSaved}
        />
      )}

      {importOpen && (
        <ImportPlaylistDialog onClose={() => setImportOpen(false)} />
      )}
    </div>
  );
}
